import { timestampDate } from '@bufbuild/protobuf/wkt'

import type { JobLite, JobPaymentSegment, PipelineStageBucket } from '@/gen/cortado/v1/jobs_pb'
import {
  InquiryStageEnum,
  JobBoardStageEnum,
  JobPaymentStateEnum,
  JobPhaseEnum,
  JobSortDirectionEnum,
  JobSortFieldEnum,
  PaymentScheduleStatusEnum,
  QuoteStatusEnum,
} from '@/gen/cortado/v1/settings_pb'
import { formatFullName } from '@/lib/utils'
import { formatShortDate } from '@/lib/date-utils'
import { formatCurrencyWhole } from '@/lib/currency-utils'
import type { OrganizationUser } from '@/gen/cortado/v1/organizations_pb'

export function pipelineJobCustomerName(job: JobLite): string {
  return formatFullName(job.contactFirstName, job.contactLastName)
}

export function pipelineJobInitials(job: JobLite): string {
  return `${job.contactFirstName?.[0] ?? ''}${job.contactLastName?.[0] ?? ''}`.toUpperCase() || '?'
}

/** "—" placeholder for a job with no contact company on file. */
export function pipelineCompanyName(job: JobLite): string {
  return job.contactCompanyName || '—'
}

export type PipelinePhaseSegmentState = 'done' | 'current' | 'future'

/**
 * State of each of the 3 stepper segments (lead / quote / booking). A
 * Complete job has all 3 done — there's no "current" step left to highlight.
 */
export function pipelinePhaseSegments(phase: JobPhaseEnum): [PipelinePhaseSegmentState, PipelinePhaseSegmentState, PipelinePhaseSegmentState] {
  if (phase === JobPhaseEnum.JOB_PHASE_COMPLETE) return ['done', 'done', 'done']
  const stage = phase === JobPhaseEnum.JOB_PHASE_QUOTING ? 1 : phase === JobPhaseEnum.JOB_PHASE_BOOKED ? 2 : 0
  const seg = (i: number): PipelinePhaseSegmentState => (i < stage ? 'done' : i === stage ? 'current' : 'future')
  return [seg(0), seg(1), seg(2)]
}

/** The Lead / Quote / Booking strip's steps, by index. */
export const PIPELINE_PHASE_STEPS = ['Lead', 'Quote', 'Booking'] as const
export type PipelinePhaseStep = (typeof PIPELINE_PHASE_STEPS)[number]

const STEP_LAYOUT_PHASE: Record<PipelinePhaseStep, JobPhaseEnum> = {
  Lead: JobPhaseEnum.JOB_PHASE_NEW,
  Quote: JobPhaseEnum.JOB_PHASE_QUOTING,
  Booking: JobPhaseEnum.JOB_PHASE_BOOKED,
}

/** The job page's `?phase=` values, one per strip step. */
export const PIPELINE_PHASE_PARAMS = ['lead', 'quote', 'booking'] as const
export type PipelinePhaseParam = (typeof PIPELINE_PHASE_PARAMS)[number]

const PARAM_STEP: Record<PipelinePhaseParam, PipelinePhaseStep> = { lead: 'Lead', quote: 'Quote', booking: 'Booking' }

export function pipelinePhaseStepFromParam(param: PipelinePhaseParam | undefined): PipelinePhaseStep | undefined {
  return param ? PARAM_STEP[param] : undefined
}

export function pipelinePhaseParam(step: PipelinePhaseStep): PipelinePhaseParam {
  return step.toLowerCase() as PipelinePhaseParam
}

/** The strip step a job's phase sits on; a complete job sits on Booking. */
export function pipelinePhaseStep(phase: JobPhaseEnum): PipelinePhaseStep {
  if (phase === JobPhaseEnum.JOB_PHASE_QUOTING) return 'Quote'
  if (phase === JobPhaseEnum.JOB_PHASE_BOOKED || phase === JobPhaseEnum.JOB_PHASE_COMPLETE) return 'Booking'
  return 'Lead'
}

/**
 * The phase the job page lays its content out for. Viewing an earlier step
 * lays the page out as that step did; viewing the job's own step (or none)
 * keeps its real phase, so a complete job stays complete.
 */
export function pipelineLayoutPhase(phase: JobPhaseEnum, viewStep: PipelinePhaseStep | undefined): JobPhaseEnum {
  if (!viewStep || viewStep === pipelinePhaseStep(phase)) return phase
  return STEP_LAYOUT_PHASE[viewStep]
}

export const PIPELINE_SEGMENT_CLASS: Record<PipelinePhaseSegmentState, string> = {
  done: 'bg-foreground',
  current: 'bg-brand',
  future: 'bg-border',
}

/**
 * Status-icon color for the "current" Lead segment, driven by the
 * linked inquiry's stage — same gate the job detail page uses to show
 * `PipelineLeadStageControl` (`job.inquiryId && job.phase === JOB_PHASE_NEW`).
 * New reads as an open/blue lead (matching the Badge `info` variant), Stale
 * as muted, Rejected as destructive (matching the stage picker's own
 * destructive text), and every other stage (including the still-open In
 * Progress and an inquiry not yet loaded) keeps the brand/amber default.
 */
export function pipelineLeadStageIconClass(stage: InquiryStageEnum | undefined): string {
  switch (stage) {
    case InquiryStageEnum.INQUIRY_STAGE_NEW:
      return 'text-status-info-fg'
    case InquiryStageEnum.INQUIRY_STAGE_STALE:
    case InquiryStageEnum.INQUIRY_STAGE_REFERRED:
      return 'text-muted-foreground'
    case InquiryStageEnum.INQUIRY_STAGE_REJECTED:
      return 'text-destructive'
    default:
      return 'text-brand'
  }
}

// ─── board stage (server-derived, canonical) ───

/**
 * All 8 real board stages, in fixed pipeline order — matches `stage_buckets`.
 * The enum's numeric values are NOT in pipeline order (QUOTE_DECLINED = 9
 * sorts after BOOKING/COMPLETE numerically), so this list must stay a fixed
 * order, never a numeric sort of the enum.
 */
export const PIPELINE_BOARD_STAGES: JobBoardStageEnum[] = [
  JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW,
  JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS,
  JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT,
  JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED,
  JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE,
]

/** Which phase tile a board stage belongs to, for filtering visible columns. */
export const PIPELINE_BOARD_STAGE_TILE: Record<JobBoardStageEnum, PipelineTileKey> = {
  [JobBoardStageEnum.JOB_BOARD_STAGE_UNSPECIFIED]: 'all',
  [JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW]: 'lead',
  [JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS]: 'lead',
  [JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT]: 'quote',
  [JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED]: 'quote',
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED]: 'booking',
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT]: 'booking',
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED]: 'booking',
  [JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE]: 'booking',
}

/**
 * Word for a lapsed quote in the QUOTE_DECLINED board-stage bucket — that
 * bucket groups Rejected, Expired, and Cancelled quotes together (see
 * `job_state.go`), so the Phase cell's info text uses the quote's own
 * `latest_quote_status` to say which, instead of always claiming "declined".
 */
function pipelineQuoteDeclinedWord(status: QuoteStatusEnum): string {
  switch (status) {
    case QuoteStatusEnum.QUOTE_STATUS_EXPIRED:
      return 'expired'
    case QuoteStatusEnum.QUOTE_STATUS_CANCELLED:
      return 'cancelled'
    default:
      return 'declined'
  }
}

function pipelinePhasePaymentWord(state: JobPaymentStateEnum): string | undefined {
  switch (state) {
    case JobPaymentStateEnum.JOB_PAYMENT_STATE_PARTIAL:
      return 'partially paid'
    case JobPaymentStateEnum.JOB_PAYMENT_STATE_PAID:
      return 'paid in full'
    case JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE:
      return 'balance overdue'
    default:
      return undefined
  }
}

/**
 * Lowercase-ish status copy for the Phase cell, driven by the server-derived
 * `board_stage` — no more client-side approximation from `waiting_on`/
 * `payment_state` alone.
 *
 * The Quote phase now only starts once a quote is sent — a job with just a
 * draft/revised quote is still board_stage LEAD_IN_PROGRESS, so its quote
 * number (when known) is surfaced under the "Lead ·" prefix rather than
 * guessing draft-vs-revised: JobLite has no latest-quote-status field to
 * distinguish them.
 */
export function pipelinePhaseStatusText(job: JobLite): string {
  const quoteLabel = job.latestQuoteNumber ? `Quote #${job.latestQuoteNumber}` : 'Quote'
  const paymentWord = pipelinePhasePaymentWord(job.paymentState)
  switch (job.boardStage) {
    case JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW:
      return 'Lead · new'
    case JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS:
      return job.latestQuoteNumber ? `Lead · ${quoteLabel} draft` : 'Lead · in progress'
    case JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT:
      return `${quoteLabel} sent`
    case JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED:
      return `${quoteLabel} ${pipelineQuoteDeclinedWord(job.latestQuoteStatus)}`
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED:
      return 'Booking · not invoiced'
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT:
      return 'Booking · pending deposit'
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED:
      return paymentWord ? `Booking · confirmed · ${paymentWord}` : 'Booking · confirmed'
    case JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE:
      return paymentWord ? `Booking · complete · ${paymentWord}` : 'Booking · complete'
    default:
      // No board_stage yet (shouldn't happen once the backend always derives
      // one) — fall back to the coarser phase.
      return job.phase === JobPhaseEnum.JOB_PHASE_QUOTING
        ? quoteLabel
        : job.phase === JobPhaseEnum.JOB_PHASE_BOOKED || job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE
          ? 'Booking'
          : 'Lead'
  }
}

/** Phase/board-stage name shown as the Phase cell's title row — always one of Lead/Quote/Booking. */
function pipelinePhaseTitle(job: JobLite): string {
  switch (job.boardStage) {
    case JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW:
    case JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS:
      return 'Lead'
    case JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT:
    case JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED:
      return 'Quote'
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED:
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT:
    case JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED:
    case JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE:
      return 'Booking'
    default:
      return job.phase === JobPhaseEnum.JOB_PHASE_QUOTING
        ? 'Quote'
        : job.phase === JobPhaseEnum.JOB_PHASE_BOOKED || job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE
          ? 'Booking'
          : 'Lead'
  }
}

/**
 * Splits `pipelinePhaseStatusText` into a title row (Lead/Quote/Booking) and
 * an info row (everything else, with the leading name and its separator
 * stripped) — so the Phase cell can render a fixed title/bars/info layout
 * that lines up with the Payments cell instead of one combined string.
 * `info` is `''` when the status text is just the name (e.g. "Booking"
 * alone never happens today, but the split stays defined for it).
 */
export function pipelinePhaseTitleAndInfo(job: JobLite): { title: string; info: string } {
  const title = pipelinePhaseTitle(job)
  const full = pipelinePhaseStatusText(job)
  if (!full.startsWith(title)) return { title, info: full }
  const info = full.slice(title.length).replace(/^\s*·?\s*/, '')
  return { title, info }
}

// ─── shared cell layout (Phase + Payments columns line up) ───

/** Flex column wrapper shared by the Phase and Payments cells. */
export const PIPELINE_CELL_STACK_CLASS = 'flex min-w-0 flex-col gap-1.5'
/** Segment-bar row — fixed height so the row still reserves space with zero segments. */
export const PIPELINE_CELL_BAR_ROW_CLASS = 'flex h-1 gap-0.5'
/** Title row: same size/weight/line-height in both columns. */
export const PIPELINE_CELL_TITLE_CLASS = 'truncate text-sm font-medium'
/** Info row: same size/line-height; callers add their own color (muted or tone-colored). */
export const PIPELINE_CELL_INFO_CLASS = 'truncate text-xs'

/** "Invoices on this booking" once a job has reached Booking/Complete, "Invoices on this job" before that. */
export function pipelineInvoicePopoverTitle(job: JobLite): string {
  return job.phase === JobPhaseEnum.JOB_PHASE_BOOKED || job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE
    ? 'Invoices on this booking'
    : 'Invoices on this job'
}

/**
 * Quoted/paid popover title: invoices are the primary list once any exist,
 * so the invoice title stands. With no invoices yet but quotes on file, the
 * popover shows quotes instead — title it accordingly.
 */
export function pipelineQuotedPaidPopoverTitle(job: JobLite, hasInvoices: boolean): string {
  return hasInvoices ? pipelineInvoicePopoverTitle(job) : 'Quotes on this job'
}

/** True when the job actually has a quote — by amount or by quote number, not by payment_state. */
export function pipelineHasQuote(job: JobLite): boolean {
  return (job.quotedCents ?? 0n) > 0n || !!job.latestQuoteNumber
}

/** True when the job has at least one non-cancelled invoice — `payment_state` is NONE otherwise. */
export function pipelineHasInvoice(job: JobLite): boolean {
  return (
    job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_UNPAID ||
    job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_PARTIAL ||
    job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_PAID ||
    job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE
  )
}

function pipelineDollarText(cents: bigint | undefined): string {
  return `$${formatCurrencyWhole(Number(cents ?? 0n))}`
}

/**
 * Headline amount: `invoiced_cents` once an invoice exists (whether or not a
 * quote preceded it), else the quoted amount, else "—" for a job with
 * neither on file.
 */
export function pipelineQuotedAmountText(job: JobLite): string {
  if (pipelineHasInvoice(job)) return pipelineDollarText(job.invoicedCents)
  if (pipelineHasQuote(job)) return pipelineDollarText(job.quotedCents)
  return '—'
}

export type PipelinePaymentsDocKind = 'quote' | 'invoice' | 'none'

/**
 * Which document backs the Payments cell — drives the title-row icon
 * (Invoice vs Quote), the amount's color (muted while quote-only), and
 * whether its segments render at full strength or tinted.
 */
export function pipelinePaymentsDocKind(job: JobLite): PipelinePaymentsDocKind {
  if (pipelineHasInvoice(job)) return 'invoice'
  if (pipelineHasQuote(job)) return 'quote'
  return 'none'
}

/** Amount text color: muted while quote-only, normal (inherited) once invoiced or with nothing on file. */
export function pipelineQuotedAmountClass(job: JobLite): string {
  return pipelinePaymentsDocKind(job) === 'quote' ? 'text-muted-foreground' : ''
}

export type PipelinePaymentSegmentTone = 'paid' | 'overdue' | 'partial' | 'neutral'

/**
 * Tone for one payment-schedule segment. PAID/WAIVED read as settled (green),
 * OVERDUE is red, a real PARTIAL payment is amber, and everything else
 * (PENDING, UNPAID, PAYMENT_INITIATED, REFUNDED, UNKNOWN, ...) stays neutral
 * grey — a quote-only job's segments (never paid) always land here.
 */
export function pipelinePaymentSegmentTone(status: PaymentScheduleStatusEnum): PipelinePaymentSegmentTone {
  switch (status) {
    case PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID:
    case PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_WAIVED:
      return 'paid'
    case PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE:
      return 'overdue'
    case PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PARTIAL:
      return 'partial'
    default:
      return 'neutral'
  }
}

/** Segment fill color per tone — reuses the same status color tokens as the sublabel/phase bar. */
export const PIPELINE_PAYMENT_SEGMENT_CLASS: Record<PipelinePaymentSegmentTone, string> = {
  paid: 'bg-status-success-fg',
  overdue: 'bg-status-danger-fg',
  partial: 'bg-status-warning-fg',
  neutral: 'bg-border',
}

/** Tinted (40%) segment fill per tone, for a quote-only row's segments — same tone, lower strength. */
export const PIPELINE_PAYMENT_SEGMENT_TINT_CLASS: Record<PipelinePaymentSegmentTone, string> = {
  paid: 'bg-status-success-fg/40',
  overdue: 'bg-status-danger-fg/40',
  partial: 'bg-status-warning-fg/40',
  neutral: 'bg-border/40',
}

/** Segment fill class for a tone: full strength once invoiced, tinted for a quote-only row. */
export function pipelinePaymentSegmentFillClass(tone: PipelinePaymentSegmentTone, docKind: PipelinePaymentsDocKind): string {
  return docKind === 'quote' ? PIPELINE_PAYMENT_SEGMENT_TINT_CLASS[tone] : PIPELINE_PAYMENT_SEGMENT_CLASS[tone]
}

/** Text color per tone — same tokens, for the Payments sublabel. */
export const PIPELINE_TONE_TEXT_CLASS: Record<PipelinePaymentSegmentTone, string> = {
  paid: 'text-status-success-fg',
  overdue: 'text-status-danger-fg',
  partial: 'text-status-warning-fg',
  neutral: 'text-muted-foreground',
}

/**
 * Tone for a quote-only row's segments/label, driven by `latest_quote_status`
 * — reuses the same palette as invoiced segments: DRAFT is neutral, a live
 * quote (sent/viewed/revised) is amber, accepted is green, and a
 * lapsed/declined/cancelled quote is red.
 */
export function pipelineQuoteTone(status: QuoteStatusEnum): PipelinePaymentSegmentTone {
  switch (status) {
    case QuoteStatusEnum.QUOTE_STATUS_SENT:
    case QuoteStatusEnum.QUOTE_STATUS_VIEWED:
    case QuoteStatusEnum.QUOTE_STATUS_REVISED:
      return 'partial'
    case QuoteStatusEnum.QUOTE_STATUS_ACCEPTED:
      return 'paid'
    case QuoteStatusEnum.QUOTE_STATUS_EXPIRED:
    case QuoteStatusEnum.QUOTE_STATUS_REJECTED:
    case QuoteStatusEnum.QUOTE_STATUS_CANCELLED:
      return 'overdue'
    default:
      return 'neutral'
  }
}

export interface PipelinePaymentSegmentDisplay {
  tone: PipelinePaymentSegmentTone
  label: string
}

/**
 * Per-segment render info for the Payments cell's bar: invoiced segments are
 * colored by their own schedule status; quote-only segments (no invoice yet)
 * are colored uniformly by `latest_quote_status` instead of each segment's
 * individual (synthetic) status. Both render as solid `flex-1 rounded-sm`
 * bars — only the tone differs.
 */
export function pipelinePaymentSegmentDisplay(job: JobLite, segment: JobPaymentSegment): PipelinePaymentSegmentDisplay {
  const tone = pipelineHasInvoice(job) ? pipelinePaymentSegmentTone(segment.status) : pipelineQuoteTone(job.latestQuoteStatus)
  return { tone, label: pipelinePaymentSegmentLabel(segment) }
}

const PIPELINE_PAYMENT_SEGMENT_STATUS_LABEL: Record<PaymentScheduleStatusEnum, string> = {
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_UNKNOWN]: 'Unknown',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING]: 'Pending',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PARTIAL]: 'Partial',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID]: 'Paid',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE]: 'Overdue',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_CANCELLED]: 'Cancelled',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_WAIVED]: 'Waived',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_UNPAID]: 'Unpaid',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED]: 'Payment submitted',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_REFUNDED]: 'Refunded',
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_DISPUTED]: 'Disputed',
}

/** "$2,100 · Paid · due Oct 3" (no "· due ..." when the segment has no due date). */
export function pipelinePaymentSegmentLabel(segment: JobPaymentSegment): string {
  const amount = pipelineDollarText(segment.amountCents)
  const status = PIPELINE_PAYMENT_SEGMENT_STATUS_LABEL[segment.status]
  const due = segment.dueDate ? ` · due ${formatShortDate(segment.dueDate)}` : ''
  return `${amount} · ${status}${due}`
}

const PIPELINE_QUOTE_STATUS_LABEL: Record<QuoteStatusEnum, string> = {
  [QuoteStatusEnum.QUOTE_STATUS_UNKNOWN]: 'Quoted',
  [QuoteStatusEnum.QUOTE_STATUS_DRAFT]: 'Quote draft',
  [QuoteStatusEnum.QUOTE_STATUS_SENT]: 'Quote sent',
  [QuoteStatusEnum.QUOTE_STATUS_VIEWED]: 'Quote viewed',
  [QuoteStatusEnum.QUOTE_STATUS_REVISED]: 'Quote revised',
  [QuoteStatusEnum.QUOTE_STATUS_ACCEPTED]: 'Accepted · not invoiced',
  [QuoteStatusEnum.QUOTE_STATUS_REJECTED]: 'Quote declined',
  [QuoteStatusEnum.QUOTE_STATUS_EXPIRED]: 'Quote expired',
  [QuoteStatusEnum.QUOTE_STATUS_CANCELLED]: 'Quote cancelled',
}

/**
 * Status label under the quoted/invoiced amount, driven by the actual
 * document data (`payment_state` once invoiced, `latest_quote_status` while
 * quote-only) — no more approximation from `board_stage`.
 */
export function pipelineQuotedSubLabel(job: JobLite): string {
  if (pipelineHasInvoice(job)) {
    switch (job.paymentState) {
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_PAID:
        return 'Paid in full'
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_PARTIAL:
        return `${pipelineDollarText(job.paidCents)} of ${pipelineDollarText(job.invoicedCents)} paid`
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE:
        return `Overdue · ${pipelineDollarText(job.dueCents)} due`
      default:
        return `Invoiced · ${pipelineDollarText(job.paidCents)} paid`
    }
  }
  if (pipelineHasQuote(job)) return PIPELINE_QUOTE_STATUS_LABEL[job.latestQuoteStatus]
  return 'Not quoted'
}

/**
 * Green only once fully paid, red only when genuinely overdue, amber for a
 * real partial payment — same tone for a quote-only row via
 * `pipelineQuoteTone`, so the label always matches its segment bars' color.
 */
export function pipelineQuotedSubLabelClass(job: JobLite): string {
  if (pipelineHasInvoice(job)) {
    switch (job.paymentState) {
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_PAID:
        return PIPELINE_TONE_TEXT_CLASS.paid
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE:
        return PIPELINE_TONE_TEXT_CLASS.overdue
      case JobPaymentStateEnum.JOB_PAYMENT_STATE_PARTIAL:
        return PIPELINE_TONE_TEXT_CLASS.partial
      default:
        return PIPELINE_TONE_TEXT_CLASS.neutral
    }
  }
  if (pipelineHasQuote(job)) return PIPELINE_TONE_TEXT_CLASS[pipelineQuoteTone(job.latestQuoteStatus)]
  return PIPELINE_TONE_TEXT_CLASS.neutral
}

/** "Aug 14 – 15, 2026" for a same-month range, "Aug 30 – Sep 1, 2026" across months, or a single date. */
export function pipelineEventDateRange(job: JobLite): string {
  if (!job.firstEventDate) return '—'
  const start = timestampDate(job.firstEventDate)
  const startText = formatShortDate(job.firstEventDate)
  if (!job.lastEventDate) return `${startText}, ${start.getFullYear()}`
  const end = timestampDate(job.lastEventDate)
  const endText = formatShortDate(job.lastEventDate)
  if (endText === startText) return `${startText}, ${start.getFullYear()}`
  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
  return sameMonth
    ? `${startText} – ${end.getDate()}, ${end.getFullYear()}`
    : `${startText} – ${endText}, ${end.getFullYear()}`
}

/** "2 events" / "1 event" / "No events yet" — without the guest count. */
export function pipelineEventsCountLabel(job: JobLite): string {
  if (job.eventCount <= 0) return 'No events yet'
  return job.eventCount === 1 ? '1 event' : `${job.eventCount} events`
}

/** "40 guests" / "1 guest" — `undefined` when `guest_count` is unset. */
export function pipelineGuestsLabel(job: JobLite): string | undefined {
  if (job.guestCount == null) return undefined
  return job.guestCount === 1 ? '1 guest' : `${job.guestCount} guests`
}

/** "2 events · 40 guests" / "1 event" (guest count omitted when unknown) / "No events yet". */
export function pipelineEventsMeta(job: JobLite): string {
  const eventsWord = pipelineEventsCountLabel(job)
  const guestsWord = pipelineGuestsLabel(job)
  return guestsWord ? `${eventsWord} · ${guestsWord}` : eventsWord
}

export function pipelineOwnerName(owner: OrganizationUser | undefined): string {
  if (!owner) return 'Unassigned'
  return formatFullName(owner.firstName, owner.lastName, owner.email)
}

export function pipelineOwnerInitials(owner: OrganizationUser | undefined): string {
  if (!owner) return '?'
  return `${owner.firstName[0] ?? ''}${owner.lastName[0] ?? ''}`.toUpperCase() || '?'
}

/** Builds a lookup of organization users by their `User.id`, for joining `JobLite.assigned_to`. */
export function buildOwnersById(users: OrganizationUser[]): Map<string, OrganizationUser> {
  const map = new Map<string, OrganizationUser>()
  for (const user of users) {
    if (user.user?.id) map.set(user.user.id, user)
  }
  return map
}

// ─── phase tiles ───

/**
 * Tile keys, in the prototype's own vocabulary. "due" is a cross-phase money
 * filter (any job with an outstanding balance), not a phase — it has no
 * corresponding `JobPhaseEnum` value.
 */
export type PipelineTileKey = 'all' | 'lead' | 'quote' | 'booking' | 'due'

export const PIPELINE_TILE_LABEL: Record<PipelineTileKey, string> = {
  all: 'All',
  lead: 'Lead',
  quote: 'Quote',
  booking: 'Booking',
  due: 'Balance due',
}

/** Human name for the tile, used in the "Showing N of M · {name}" toolbar copy. */
export const PIPELINE_TILE_FILTER_NAME: Record<PipelineTileKey, string> = {
  all: 'all phases',
  lead: 'Lead',
  quote: 'Quote',
  booking: 'Booking',
  due: 'records with a balance due',
}

/**
 * Maps a tile selection to `ListJobsRequest.filters` fields. "booking" spans
 * both Booked and Complete (the prototype has no separate Complete phase).
 * "due" has no phase at all — it filters on payment_state instead, since
 * `GetPipelineSummary` doesn't expose a balance-due job list directly.
 */
export function pipelineTileToListFilters(
  tile: PipelineTileKey,
): { phases: JobPhaseEnum[]; paymentStates: JobPaymentStateEnum[] } {
  switch (tile) {
    case 'lead':
      return { phases: [JobPhaseEnum.JOB_PHASE_NEW], paymentStates: [] }
    case 'quote':
      return { phases: [JobPhaseEnum.JOB_PHASE_QUOTING], paymentStates: [] }
    case 'booking':
      return { phases: [JobPhaseEnum.JOB_PHASE_BOOKED, JobPhaseEnum.JOB_PHASE_COMPLETE], paymentStates: [] }
    case 'due':
      return {
        phases: [],
        paymentStates: [
          JobPaymentStateEnum.JOB_PAYMENT_STATE_UNPAID,
          JobPaymentStateEnum.JOB_PAYMENT_STATE_PARTIAL,
          JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE,
        ],
      }
    default:
      return { phases: [], paymentStates: [] }
  }
}

// ─── lead-column drag/menu (New ↔ In Progress) ───

/** The only two board-stage columns a Pipeline board card can move between. */
export const PIPELINE_LEAD_DRAG_STAGES: ReadonlySet<JobBoardStageEnum> = new Set([
  JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW,
  JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS,
])

/**
 * Resolves a Pipeline board drag-and-drop as an inquiry-stage change, or
 * `null` when the drop isn't a valid New ↔ In Progress move — either side
 * isn't a lead column, or the card was dropped back on its own column.
 */
export function pipelineLeadDropStage(
  sourceStage: JobBoardStageEnum,
  targetStage: JobBoardStageEnum,
): InquiryStageEnum | null {
  if (sourceStage === targetStage) return null
  if (!PIPELINE_LEAD_DRAG_STAGES.has(sourceStage) || !PIPELINE_LEAD_DRAG_STAGES.has(targetStage)) return null
  return targetStage === JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW
    ? InquiryStageEnum.INQUIRY_STAGE_NEW
    : InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS
}

/**
 * The other lead stage a card can be moved to via the card's stage-toggle
 * action, or `undefined` for a card that isn't in a lead column at all.
 */
export function pipelineOtherLeadStage(
  stage: JobBoardStageEnum,
): { inquiryStage: InquiryStageEnum; label: string } | undefined {
  if (stage === JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW) {
    return { inquiryStage: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS, label: 'Move to In Progress' }
  }
  if (stage === JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS) {
    return { inquiryStage: InquiryStageEnum.INQUIRY_STAGE_NEW, label: 'Move to New' }
  }
  return undefined
}

/** Looks up one stage's bucket from `GetPipelineSummaryResponse.stage_buckets` (always 8, enum order). */
export function pipelineStageBucket(
  buckets: PipelineStageBucket[],
  stage: JobBoardStageEnum,
): PipelineStageBucket | undefined {
  return buckets.find((bucket) => bucket.stage === stage)
}

/**
 * Total job count for a tile from `GetPipelineSummary.buckets` (phase
 * buckets) — the same totals the phase tiles themselves show. "all" and
 * "due" both show every board-stage column, so both use the org-wide total.
 */
export function pipelineTileJobCount(
  buckets: { bucket: JobPhaseEnum; count: number }[],
  tile: PipelineTileKey,
): number {
  switch (tile) {
    case 'lead':
      return buckets.find((bucket) => bucket.bucket === JobPhaseEnum.JOB_PHASE_NEW)?.count ?? 0
    case 'quote':
      return buckets.find((bucket) => bucket.bucket === JobPhaseEnum.JOB_PHASE_QUOTING)?.count ?? 0
    case 'booking':
      return (
        (buckets.find((bucket) => bucket.bucket === JobPhaseEnum.JOB_PHASE_BOOKED)?.count ?? 0) +
        (buckets.find((bucket) => bucket.bucket === JobPhaseEnum.JOB_PHASE_COMPLETE)?.count ?? 0)
      )
    case 'all':
    case 'due':
    default:
      return buckets.reduce((sum, bucket) => sum + bucket.count, 0)
  }
}

// ─── sort ───

export const PIPELINE_SORT_FIELD_LABEL: Record<JobSortFieldEnum, string> = {
  [JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED]: '',
  [JobSortFieldEnum.JOB_SORT_FIELD_CUSTOMER_NAME]: 'customer',
  [JobSortFieldEnum.JOB_SORT_FIELD_FIRST_EVENT_DATE]: 'event date',
  [JobSortFieldEnum.JOB_SORT_FIELD_BOARD_STAGE]: 'stage',
  [JobSortFieldEnum.JOB_SORT_FIELD_QUOTED]: 'quoted amount',
  [JobSortFieldEnum.JOB_SORT_FIELD_PAID]: 'paid amount',
  [JobSortFieldEnum.JOB_SORT_FIELD_OWNER]: 'owner',
  [JobSortFieldEnum.JOB_SORT_FIELD_UPDATED_AT]: 'last updated',
}

export interface PipelineSort {
  field: JobSortFieldEnum
  direction: JobSortDirectionEnum
}

/** Cycles a column's sort: unsorted → asc → desc → unsorted (default order). */
export function pipelineNextSort(current: PipelineSort, field: JobSortFieldEnum): PipelineSort {
  if (current.field !== field) return { field, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_ASC }
  if (current.direction === JobSortDirectionEnum.JOB_SORT_DIRECTION_ASC) {
    return { field, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC }
  }
  return { field: JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_UNSPECIFIED }
}

/** `aria-sort` value for a `<th>` bound to `field`. */
export function pipelineAriaSort(sort: PipelineSort, field: JobSortFieldEnum): 'ascending' | 'descending' | 'none' {
  if (sort.field !== field) return 'none'
  return sort.direction === JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC ? 'descending' : 'ascending'
}

function pipelineSortText(sort: PipelineSort): string {
  return sort.field === JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED
    ? 'sorted by phase, then next action'
    : `sorted by ${PIPELINE_SORT_FIELD_LABEL[sort.field]} (${sort.direction === JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC ? 'descending' : 'ascending'})`
}

/** "Showing N of M · {tile} · sorted by {field} (ascending|descending)" / the default order description. */
export function pipelineShowingLabel(loaded: number, total: number, tileFilterName: string, sort: PipelineSort): string {
  return `Showing ${loaded} of ${total} · ${tileFilterName} · ${pipelineSortText(sort)}`
}

/**
 * Board-view toolbar text. The board's columns each paginate independently
 * (no single "loaded of total" like the table), so this reads the tile's
 * true total from the summary instead of a loaded-row count.
 */
export function pipelineBoardShowingLabel(totalJobs: number, tileFilterName: string, sort: PipelineSort): string {
  const jobsWord = totalJobs === 1 ? '1 job' : `${totalJobs} jobs`
  return `${jobsWord} · ${tileFilterName} · ${pipelineSortText(sort)}`
}
